'use client';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import '../../../../polyfills';
import {
  type UIEvent,
  type UIEventHandler,
  useLayoutEffect,
  useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { Document, type DocumentProps, Page, pdfjs } from 'react-pdf';
import { sortNumerical } from '@witmetrics/utils/arrays';
import { isNullish } from '@witmetrics/utils/misc';
import type { Dictionary } from '@witmetrics/utils';
import { showErrorToaster } from '@/utils/toasters';
import { useNumber } from '@/hooks/useNumber';
import PdfControls, { type PdfControlsProps } from './PdfControls';
import type { PageCoords } from './types';
import { SCALES } from './constants';
import { classes, getPageMarkerClass, getPageScrollTop } from './utils';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export { PdfControls, type PdfControlsProps };

export type PdfViewerProps = {
  className?: string;
  controlsClassName?: string;
  documentClassName?: string;
  src: File | Blob | string;
};

export default function PdfViewer({
  className,
  controlsClassName,
  documentClassName,
  src,
}: PdfViewerProps) {
  const isLoadedRef = useRef(false);
  const documentRef = useRef<HTMLDivElement>(null);
  const checkScrollRef = useRef(true); // Disable scroll check when programmatically changing pages
  const pageCoordsRef = useRef<Dictionary<PageCoords>>();
  const [pdfKey, setPdfKey] = useNumber(0); // See https://github.com/wojtekmaj/react-pdf/issues/974#issuecomment-3293069045
  const [page, setPage] = useNumber(1);
  const [pageCount, setPageCount] = useNumber(0);
  const [scale, setScale] = useNumber(1);

  useLayoutEffect(() => {
    const observer = new MutationObserver((records, observer) => {
      if (pageCount === 0 || isLoadedRef.current) return;
      handleAutoScale();
      isLoadedRef.current = true;
      observer.disconnect();
    });
    if (documentRef.current) {
      observer.observe(documentRef.current, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, [documentRef.current]);

  useLayoutEffect(() => {
    setPdfKey((key) => key + 1);
  }, [src]);

  const handleDocumentLoadSuccess: DocumentProps['onLoadSuccess'] = (pdf) => {
    setPageCount(pdf.numPages);
  };

  const handleDocumentLoadError = (err: Error) => {
    showErrorToaster('Error loading PDF');
    throw err;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
    const top = getPageScrollTop(documentRef.current!, newPage);
    if (isNullish(top)) return;
    checkScrollRef.current = false;
    documentRef.current!.scrollTo({ top, behavior: 'smooth' });
  };

  const handleAutoScale = () => {
    const docBox = documentRef.current!.getBoundingClientRect();
    const pageBox = documentRef
      .current!.querySelector('.js-page')
      ?.getBoundingClientRect();
    if (!pageBox) return;
    if (pageBox.width > docBox.width) {
      const fitScale = docBox.width / pageBox.width;
      // Find the closest scale option to the fitScale
      const closestScale = sortNumerical(SCALES, -1).find((s) => s <= fitScale);
      setScale(closestScale || 1);
    }
  };

  const handleScaleChange = (newScale: number) => {
    setScale(newScale);
    pageCoordsRef.current = undefined;
  };

  const calculatePageCoords = () => {
    // eslint-disable-next-line witmetrics/no-multiline-comments
    /*
        `getBoundingClientRect` doesn't take scroll position into account, need to offset it.
        Also doesn't factor in margins, need to parse from computed styles
        Scrolling is relative to the parent node so we also need to offset the page's Y coord by the parent
     */
    let result: Dictionary<PageCoords> = {};
    const wrapperBox = documentRef.current!.getBoundingClientRect();
    const scrollTop = documentRef.current!.scrollTop;
    Array.from(documentRef.current!.children).forEach((child, i) => {
      const coords = child.getBoundingClientRect();
      const { marginTop, marginBottom } = getComputedStyle(child);
      const pageTop = coords.y - wrapperBox.y - parseInt(marginTop) + scrollTop;
      result[i + 1] = {
        pageTop,
        pageBottom: pageTop + coords.height + parseInt(marginBottom),
      };
    });
    return result;
  };

  const checkScrollPosition: UIEventHandler<HTMLDivElement> = (e: UIEvent) => {
    const scrollTop = (e.target as HTMLDivElement).scrollTop;
    const { pageTop, pageBottom } = pageCoordsRef.current![page];
    if (scrollTop < pageTop && page !== 1) {
      // Go to previous page if exists
      setPage(page - 1);
    } else if (scrollTop > pageBottom && page !== pageCount) {
      // Go to next page if exists
      setPage(page + 1);
    }
  };

  const handleScroll: UIEventHandler<HTMLDivElement> = (e) => {
    documentRef.current!.addEventListener('scrollend', handleScrollEnd);
    if (!checkScrollRef.current) return;
    if (!pageCoordsRef.current) pageCoordsRef.current = calculatePageCoords();
    checkScrollPosition(e);
  };

  const handleScrollEnd = () => {
    documentRef.current!.removeEventListener('scrollend', handleScrollEnd);
    if (!checkScrollRef.current) checkScrollRef.current = true;
  };

  const renderPages = () => {
    const pages = [];
    for (let i = 1; i <= pageCount; i++) {
      pages.push(
        <Page
          key={i}
          className={twMerge(classes.page, getPageMarkerClass(i))}
          pageNumber={i}
          scale={scale}
          loading={
            i === 1 && <div className={classes.message}>Loading page #1...</div>
          }
        />
      );
    }
    return pages;
  };

  return (
    <div className={twMerge(classes.wrapper, className)}>
      <PdfControls
        className={classes.controls}
        wrapperClassName={controlsClassName}
        page={page}
        pageCount={pageCount}
        scale={scale}
        onAutoScale={handleAutoScale}
        onPageChange={handlePageChange}
        onScaleChange={handleScaleChange}
      />
      <div className={classes.documentWrapper}>
        <Document
          key={pdfKey}
          inputRef={documentRef}
          className={twMerge(classes.document, documentClassName)}
          file={src}
          onScroll={handleScroll}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}>
          {renderPages()}
        </Document>
      </div>
    </div>
  );
}
